import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, MessageSquare, CalendarCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { initLenis } from "../lib/smooth";
import { api, formatApiError, getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG } from "../data/site";

const WA_PRICE_MSG =
  "Hello Forest Hill Realtors, I am interested in Vinayak 21 Acres. Please share the latest price and availability.";

const PRICE_FAQS = [
  {
    q: "What is the current price of Vinayak 21 Acres?",
    a: "Pricing at Vinayak 21 Acres varies by apartment configuration, tower, floor, unit selection, applicable charges and current availability. Prices and availability are subject to change. Contact Forest Hill Realtors for the latest developer-provided pricing and available inventory.",
  },
  {
    q: "What is the price of a 2 BHK in Vinayak 21 Acres?",
    a: "The project offers 2 BHK 2T residences of approximately 595–597 sq ft carpet area across Towers 1A, 1B, 2A and 2B. The price of a specific 2 BHK depends on the tower, floor and unit you choose. Share your details and we will share the latest developer-provided price for your preferred unit.",
  },
  {
    q: "What is the price of a 3 BHK in Vinayak 21 Acres?",
    a: "3 BHK homes are available in Smart, Comfort and Luxury configurations — from around 698 sq ft carpet (Smart) up to about 943 sq ft carpet (Luxury). Prices differ by category, tower and floor, so we recommend requesting the current price list for the exact unit you like.",
  },
  {
    q: "How can I get the latest Vinayak 21 Acres price list?",
    a: "Submit the enquiry form on this page, message us on WhatsApp, or call Forest Hill Realtors. We will share the latest developer-provided price list and payment details for your preferred configuration.",
  },
  {
    q: "How can I check current availability?",
    a: "Availability changes as homes are booked. Forest Hill Realtors receives developer-provided inventory updates and can confirm which towers, floors and unit types are currently open for booking.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use the Book a Site Visit button on this page, tap the sticky enquiry bar on mobile, or reach us on WhatsApp. We will arrange a convenient slot and guide you through the project in person.",
  },
];

const inputCls =
  "w-full bg-white border border-[#DCECC0] rounded-lg px-4 py-3 text-sm text-[#365314] placeholder:text-[#65A30D]/50 focus:outline-none focus:border-[#84CC16] focus:ring-2 focus:ring-[#84CC16]/20 transition-all duration-300";

export default function Price() {
  const [modal, setModal] = useState({ open: false, source: "Price Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });
  const [form, setForm] = useState({ full_name: "", phone: "", interest: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Vinayak 21 Acres Price | 2 & 3 BHK Price New Town Kolkata";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Check Vinayak 21 Acres price information for 2 & 3 BHK homes in New Town Kolkata. Contact Forest Hill Realtors for latest pricing, availability, floor plans and site visits."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_PRICE_MSG)}`;

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/leads", { ...form, source: "Price Page" });
      setStatus("success");
      toast.success("Enquiry received. Forest Hill Realtors will reach out shortly.");
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err));
    }
  };

  return (
    <div className="bg-[#FFFFFF]" data-testid="price-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="price-overline">Price Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-3xl"
              data-testid="price-h1"
            >
              Vinayak 21 Acres <span className="text-[#84CC16]">Price</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              Vinayak 21 Acres offers thoughtfully planned 2 &amp; 3 BHK residences at Hatishala, off Newtown Action
              Area III, Kolkata — designed around natural light, cross ventilation, green spaces and practical
              everyday living, with a 3-acre central park at its heart.
            </p>
            <Link
              to="/"
              data-testid="price-home-link"
              className="mt-8 inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300"
            >
              Explore Vinayak 21 Acres <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        <section data-testid="price-overview-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Price Overview" title="Vinayak 21 Acres Price Overview" center />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-10 shadow-[0_24px_48px_-24px_rgba(77,124,15,0.18)]">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                  Homes at Vinayak 21 Acres are priced individually. The price of a residence varies according to the
                  apartment configuration (2 BHK or 3 BHK), the tower, the floor, the specific unit selected,
                  applicable charges and the current availability at the time of booking.
                </p>
                <div className="mt-6 border-l-4 border-[#84CC16] bg-[#F4FAE8] rounded-r-xl p-5">
                  <p className="text-[#365314] text-sm sm:text-base font-sub font-medium leading-relaxed" data-testid="price-disclaimer">
                    Prices and availability are subject to change. Contact Forest Hill Realtors for the latest
                    developer-provided pricing and available inventory.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="price-config-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 h-full hero-grain" data-testid="price-2bhk-card">
                <p className="overline-tag">02 · 2 BHK</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] mt-5 leading-tight">
                  Vinayak 21 Acres 2 BHK Price
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 leading-relaxed">
                  The 2 BHK 2T residences — approximately 595–597 sq ft carpet area with about 60 sq ft of balcony —
                  are available across multiple towers. The exact price depends on the tower, floor and unit you
                  select, along with applicable charges.
                </p>
                <button
                  data-testid="price-get-2bhk-btn"
                  onClick={() => openModal("Get Latest 2 BHK Price", "2 BHK")}
                  className="mt-8 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300"
                >
                  Get Latest 2 BHK Price
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative overflow-hidden rounded-2xl border border-[#DCECC0] bg-white p-8 sm:p-12 h-full" data-testid="price-3bhk-card">
                <p className="overline-tag">03 · 3 BHK</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#365314] mt-5 leading-tight">
                  Vinayak 21 Acres 3 BHK Price
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 leading-relaxed">
                  3 BHK homes come in three configuration families — <strong>Smart</strong> (from approx. 698 sq ft
                  carpet), <strong>Comfort</strong> (approx. 736–783 sq ft carpet) and <strong>Luxury</strong>{" "}
                  (approx. 823–943 sq ft carpet) — each with its own pricing based on tower, floor and availability.
                </p>
                <button
                  data-testid="price-get-3bhk-btn"
                  onClick={() => openModal("Get Latest 3 BHK Price", "3 BHK")}
                  className="mt-8 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300"
                >
                  Get Latest 3 BHK Price
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="price-form-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="Enquire" title="Current Price & Availability" center />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-6 sm:p-10 mt-10 shadow-[0_24px_48px_-24px_rgba(77,124,15,0.18)]">
                {status === "success" ? (
                  <div className="text-center py-8" data-testid="price-form-success">
                    <CheckCircle2 size={40} className="text-[#84CC16] mx-auto" strokeWidth={1.4} />
                    <p className="font-serif-display text-2xl mt-5 text-[#365314]">Thank you.</p>
                    <p className="text-sm mt-2 text-[#4D7C0F] leading-relaxed">
                      Forest Hill Realtors will contact you shortly regarding Vinayak 21 Acres.
                    </p>
                    <a
                      data-testid="price-form-whatsapp-cta"
                      href={waLink}
                      target="_blank" rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white font-sub font-medium text-sm px-7 py-3.5 rounded-full hover:bg-[#1EBE57] active:scale-[0.97] transition-all duration-300"
                    >
                      <MessageSquare size={16} /> Chat with us on WhatsApp
                    </a>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-3.5" data-testid="price-form">
                    <p className="text-sm text-[#4D7C0F] leading-relaxed mb-2">
                      Tell us what you're looking for and we will share the latest developer-provided price and
                      available inventory for Vinayak 21 Acres.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3.5">
                      <input data-testid="price-form-name-input" required minLength={2} placeholder="Name *"
                        value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                      <input data-testid="price-form-phone-input" required minLength={8} type="tel" placeholder="Mobile Number *"
                        value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                    </div>
                    <select data-testid="price-form-interest-select" value={form.interest}
                      onChange={(e) => setForm({ ...form, interest: e.target.value })} className={inputCls}>
                      <option value="">Interested In</option>
                      <option value="2 BHK">2 BHK</option>
                      <option value="3 BHK">3 BHK</option>
                    </select>
                    <textarea data-testid="price-form-message-input" rows={3} placeholder="Message (optional)"
                      value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
                    {error && <p className="text-sm text-red-700" data-testid="price-form-error">{error}</p>}
                    <button data-testid="price-form-submit-btn" type="submit" disabled={status === "loading"}
                      className="w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
                      {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                      Get Latest Price &amp; Availability
                    </button>
                    <p className="text-[11px] text-center text-[#65A30D] leading-relaxed">
                      By submitting, you agree to be contacted by Forest Hill Realtors regarding Vinayak 21 Acres.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="price-faq-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="FAQs" title="Vinayak 21 Acres Price FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="price-faq-accordion">
                {PRICE_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`price-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`price-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <section data-testid="price-cta-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight">
                  Prefer to talk it through?
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Message us on WhatsApp for the latest price list, or book a guided site visit and experience the
                  homes and the 3-acre central park in person.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                  <a data-testid="price-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                  <button data-testid="price-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                </div>
                <Link to="/" data-testid="price-home-link-bottom"
                  className="mt-6 inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                  Explore Vinayak 21 Acres <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="price-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
